import { apiFetch } from '../auth/apiFetch.ts';
import { useQuery } from '@tanstack/react-query';

// Capture-scan records (pipeline/models/scans.py). The lifecycle the scan-detail
// UI renders (PRD-0006 D8): a session is registered (`raw`), the COPC worker runs
// (`processing`), and lands a `copc` derivative row on success (`copc`) — or
// `failed`. `null` predates the processing column.
export type ProcessingState = 'raw' | 'processing' | 'copc' | 'failed' | null;

export type DerivativeKind = 'copc' | (string & {});

export interface ScanDerivative {
  kind: DerivativeKind;
  storage_uri: string | null;
  size_bytes: number;
}

export interface ScanFile {
  name: string;
  content_sha256: string;
  size_bytes: number;
  storage_uri: string | null;
}

export interface Scan {
  id: number;
  robot_id: string;
  session_id: string;
  platform: string | null;
  morphology: string | null;
  format: string | null;
  sensors: string[];
  started_utc: string | null;
  stopped_utc: string | null;
  size_bytes: number;
  pose_frame: string | null;
  processing_state: ProcessingState;
  sync_state: string | null;
  created_at: string;
  updated_at: string;
  /* OPTIONAL BECAUSE THE WIRE REALLY CAN OMIT THEM. The console and the API
     deploy independently, and a list endpoint that does not attach these is a
     shape this client will meet during any deploy skew. Typing them as required
     did not prevent that -- it only stopped the compiler from pointing at the
     ~12 places that would throw, and `files.map` on undefined takes down the
     whole route, not one cell. Read them through `?? []`. */
  files?: ScanFile[];
  derivatives?: ScanDerivative[];
}

/* Read these two through here, never directly. They are optional on the wire
   (see `Scan`), and every direct `.map`/`.length` on them is a route-killing
   render fault waiting for the next deploy skew. */
export const scanFiles = (s: Scan): ScanFile[] => s.files ?? [];
export const scanDerivatives = (s: Scan): ScanDerivative[] => s.derivatives ?? [];


async function fetchScan(scanId: number): Promise<Scan> {
  const r = await apiFetch(`/api/scans/${scanId}`);
  if (!r.ok) throw new Error(`status ${r.status}`);
  return (await r.json()) as Scan;
}

// Poll while the scan is still processing so the viewer swaps in the instant the
// copc derivative lands; go quiet once it's terminal (copc / failed).
export function useScan(scanId: number | undefined) {
  return useQuery({
    queryKey: ['scan', scanId] as const,
    queryFn: () => fetchScan(scanId as number),
    enabled: scanId != null,
    refetchInterval: (query) => {
      const state = query.state.data?.processing_state;
      return state === 'copc' || state === 'failed' ? false : 5_000;
    },
  });
}

async function fetchScansForRobot(robotId: string): Promise<Scan[]> {
  const r = await apiFetch(`/api/robots/${encodeURIComponent(robotId)}/scans`);
  if (!r.ok) throw new Error(`status ${r.status}`);
  const body = (await r.json()) as { scans?: Scan[] };
  return body.scans ?? [];
}

export function useRobotScans(robotId: string | undefined) {
  return useQuery({
    queryKey: ['scans', robotId] as const,
    queryFn: () => fetchScansForRobot(robotId as string),
    enabled: !!robotId,
    refetchInterval: 15_000,
  });
}

export interface DerivativeUrl {
  kind: DerivativeKind;
  url: string;
  storage_uri: string;
  size_bytes: number;
  expires_in: number;
}

async function fetchDerivativeUrl(
  scanId: number,
  kind: DerivativeKind,
): Promise<DerivativeUrl> {
  const r = await apiFetch(`/api/scans/${scanId}/derivatives/${kind}/url`);
  if (!r.ok) throw new Error(`status ${r.status}`);
  return (await r.json()) as DerivativeUrl;
}

// Mint the short-lived presigned GET URL for a scan's COPC (PRD-0006 D8, Path B).
// The URL is Range-readable; CopcCanvas streams octree nodes from it directly.
// URLs expire (`expires_in`), so this query is refetched on that cadence — a
// long viewing session re-mints before the current URL dies.
export function useDerivativeUrl(
  scanId: number | undefined,
  kind: DerivativeKind,
  enabled = true,
) {
  return useQuery({
    queryKey: ['scan-derivative-url', scanId, kind] as const,
    queryFn: () => fetchDerivativeUrl(scanId as number, kind),
    enabled: scanId != null && enabled,
    // Re-mint a minute before the 15-min-max URL would expire; the endpoint
    // defaults to 900s, so refetch at 12 min keeps a live URL in hand.
    staleTime: 12 * 60_000,
    refetchInterval: 12 * 60_000,
    refetchOnWindowFocus: false,
  });
}
